import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";

export interface SmileClient {
    user_id: number;
    user_name: string;
    email: string;
    token: string | null;
    role: string;
    verifySmile: boolean;
    smile_credits: number;
    smile_free_credits: boolean;
    created_at: string;
    cases: number;
    generations: number;
}

export interface SmileClientsStats {
    totalClients: number;
    totalCredits: number;
    totalCases: number;
    totalGenerations: number;
}

export interface SmileClientsPagination {
    totalCount: number;
    currentPage: number;
    totalPages: number;
    limit: number;
}

interface SmileClientsState {
    clients: SmileClient[];
    stats: SmileClientsStats | null;
    pagination: SmileClientsPagination | null;
    loading: boolean;
    error: string | null;
}

const initialState: SmileClientsState = {
    clients: [],
    stats: null,
    pagination: null,
    loading: false,
    error: null,
};

export const fetchSmileClients = createAsyncThunk(
    "smileClients/fetchSmileClients",
    async (
        params: { search?: string; page?: number; limit?: number } | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get("/payment/clients/overview", {
                params,
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to fetch clients"
            );
        }
    }
);

const smileClientsSlice = createSlice({
    name: "smileClients",
    initialState,
    reducers: {
        clearSmileClientsError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSmileClients.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSmileClients.fulfilled, (state, action) => {
                state.loading = false;
                state.clients = action.payload.clients;
                state.stats = action.payload.stats;
                state.pagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })
            .addCase(fetchSmileClients.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearSmileClientsError } = smileClientsSlice.actions;

export default smileClientsSlice.reducer;
